import type {
  SetupApiPhase,
  SetupDomainPhase,
  SetupStepParam,
} from '@/components/brand-setup/resolve-home-view'

/**
 * Durable setup state is scoped to both the Clerk organization and one explicit
 * setup session. The session lives in the `/setup?session=<uuid>` URL, so two
 * add-brand tabs cannot overwrite one another and an org switch cannot consume
 * another workspace's handoff.
 */
export type SetupWizardScope = {
  orgId: string
  sessionId: string
}

type SetupWizardCookieNames = {
  step: string
  domainPhase: string
  apiPhase: string
  chat: string
  brand: string
}

const LEGACY_SETUP_WIZARD_COOKIE_NAMES = [
  'brew_setup_step',
  'brew_setup_chat',
  'brew_setup_brand',
  'brew_brand_intent',
] as const

/** One day is plenty for an onboarding session; finish/cancel clears it. */
const MAX_AGE_SECONDS = 60 * 60 * 24
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SETUP_WIZARD_STATE_EVENT = 'brew:setup-wizard-state'

function emitSetupWizardState(scope: SetupWizardScope): void {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(
    new CustomEvent<SetupWizardScope>(SETUP_WIZARD_STATE_EVENT, {
      detail: scope,
    })
  )
}

/**
 * Subscribe to durable writes for one exact setup session. This bridges late
 * route-handler completions across a first-brand React tree remount without
 * polling or relying on the new org's Convex JWT having refreshed already.
 */
export function subscribeSetupWizardState(
  scope: SetupWizardScope,
  onStoreChange: () => void
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined
  }
  const handleChange = (event: Event) => {
    const detail = (event as CustomEvent<SetupWizardScope>).detail
    if (detail?.orgId !== scope.orgId || detail.sessionId !== scope.sessionId) {
      return
    }
    onStoreChange()
  }
  window.addEventListener(SETUP_WIZARD_STATE_EVENT, handleChange)
  return () =>
    window.removeEventListener(SETUP_WIZARD_STATE_EVENT, handleChange)
}

export function isSetupSessionId(
  value: string | null | undefined
): value is string {
  return typeof value === 'string' && UUID_V4_PATTERN.test(value)
}

/**
 * Every cookie-persistable wizard step. Kept as a list so adding a step
 * can't silently leave the parser behind — an unparsed marker drops the
 * user back to the first step on reload. `satisfies` only checks entries
 * ARE valid params; the `_AllStepsParsed` assertion below checks the
 * reverse (every param has an entry), which is the direction that rots.
 */
const SETUP_STEP_PARAMS = [
  'domain',
  'api',
  'team',
  'promo',
] as const satisfies ReadonlyArray<SetupStepParam>

type _AllStepsParsed =
  Exclude<SetupStepParam, (typeof SETUP_STEP_PARAMS)[number]> extends never
    ? true
    : 'SETUP_STEP_PARAMS is missing a SetupStepParam literal'
const _stepsExhaustive: _AllStepsParsed = true
void _stepsExhaustive

export function parseSetupWizardStep(
  value: string | undefined | null
): SetupStepParam | null {
  return SETUP_STEP_PARAMS.includes(value as SetupStepParam)
    ? (value as SetupStepParam)
    : null
}

const SETUP_API_PHASES = [
  'agents',
  'taste',
  'stack',
  'tools',
] as const satisfies ReadonlyArray<SetupApiPhase>

export function parseSetupApiPhase(
  value: string | undefined | null
): SetupApiPhase | null {
  return SETUP_API_PHASES.find((phase) => phase === value) ?? null
}

const SETUP_DOMAIN_PHASES = [
  'connect',
  'complete',
] as const satisfies ReadonlyArray<SetupDomainPhase>

export function parseSetupDomainPhase(
  value: string | undefined | null
): SetupDomainPhase | null {
  return SETUP_DOMAIN_PHASES.find((phase) => phase === value) ?? null
}

function toCookieNamePart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, '_')
}

export function getSetupWizardCookieNames(
  scope: SetupWizardScope
): SetupWizardCookieNames {
  const suffix = `${toCookieNamePart(scope.orgId)}_${toCookieNamePart(scope.sessionId)}`
  return {
    step: `brew_setup_step_${suffix}`,
    domainPhase: `brew_setup_domain_phase_${suffix}`,
    apiPhase: `brew_setup_api_phase_${suffix}`,
    chat: `brew_setup_chat_${suffix}`,
    brand: `brew_setup_brand_${suffix}`,
  }
}

/**
 * Persist the dedicated domain step's rollback-compatible subphase. The
 * outer `domain` literal remains readable by the previous deployment.
 */
export function writeSetupDomainPhase(
  scope: SetupWizardScope,
  phase: SetupDomainPhase
): void {
  const names = getSetupWizardCookieNames(scope)
  writeCookie(names.step, 'domain')
  writeCookie(names.domainPhase, phase)
  emitSetupWizardState(scope)
}

/**
 * Persist one internal half of the rollback-compatible `api` cookie step.
 * Writing both cookies here prevents a phase with no enclosing legacy step.
 */
export function writeSetupApiPhase(
  scope: SetupWizardScope,
  phase: SetupApiPhase
): void {
  const names = getSetupWizardCookieNames(scope)
  writeCookie(names.step, 'api')
  writeCookie(names.apiPhase, phase)
  emitSetupWizardState(scope)
}

export function brandIntentCookieName(orgId: string): string {
  return `brew_brand_intent_${toCookieNamePart(orgId)}`
}

function writeCookie(name: string, value: string): void {
  // biome-ignore lint/suspicious/noDocumentCookie: pre-Ultracite baseline
  document.cookie = `${name}=${value}; path=/; max-age=${MAX_AGE_SECONDS}; samesite=lax`
}

function deleteCookie(name: string): void {
  // biome-ignore lint/suspicious/noDocumentCookie: pre-Ultracite baseline
  document.cookie = `${name}=; path=/; max-age=0; samesite=lax`
}

/** Client-only decoded cookie read. Returns `null` during server rendering. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  const match = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
  if (!match) {
    return null
  }
  const raw = match.slice(name.length + 1)
  return raw ? decodeURIComponent(raw) : null
}

export function writeSetupStepCookie(
  scope: SetupWizardScope,
  step: SetupStepParam
): void {
  writeCookie(getSetupWizardCookieNames(scope).step, step)
  emitSetupWizardState(scope)
}

export function writeSetupChatCookie(
  scope: SetupWizardScope,
  chatId: string
): void {
  writeCookie(getSetupWizardCookieNames(scope).chat, encodeURIComponent(chatId))
  emitSetupWizardState(scope)
}

export function writeSetupBrandCookie(
  scope: SetupWizardScope,
  brandId: string
): void {
  writeCookie(
    getSetupWizardCookieNames(scope).brand,
    encodeURIComponent(brandId)
  )
  emitSetupWizardState(scope)
}

export function clearSetupWizardCookies(scope: SetupWizardScope): void {
  const names = getSetupWizardCookieNames(scope)
  deleteCookie(names.step)
  deleteCookie(names.domainPhase)
  deleteCookie(names.apiPhase)
  deleteCookie(names.chat)
  deleteCookie(names.brand)
  emitSetupWizardState(scope)
}

/** Clear an obsolete step while preserving the idempotent chat/brand target. */
export function clearSetupStepCookie(scope: SetupWizardScope): void {
  const names = getSetupWizardCookieNames(scope)
  deleteCookie(names.step)
  deleteCookie(names.domainPhase)
  deleteCookie(names.apiPhase)
  emitSetupWizardState(scope)
}

/** Remove cookies written by pre-session deployments during rollout. */
export function clearLegacySetupWizardCookies(): void {
  for (const name of LEGACY_SETUP_WIZARD_COOKIE_NAMES) {
    deleteCookie(name)
  }
}

export function readSetupChatCookie(scope: SetupWizardScope): string | null {
  return readCookie(getSetupWizardCookieNames(scope).chat)
}

export function readSetupBrandCookie(scope: SetupWizardScope): string | null {
  return readCookie(getSetupWizardCookieNames(scope).brand)
}

export function readSetupApiPhase(
  scope: SetupWizardScope
): SetupApiPhase | null {
  return parseSetupApiPhase(
    readCookie(getSetupWizardCookieNames(scope).apiPhase)
  )
}

export function readSetupDomainPhase(
  scope: SetupWizardScope
): SetupDomainPhase | null {
  return parseSetupDomainPhase(
    readCookie(getSetupWizardCookieNames(scope).domainPhase)
  )
}

/**
 * Client-only: mark a just-ADDED brand as the one to activate.
 *
 * All three resolvers read this — `resolveActiveBrandId` (client),
 * `resolveInitialBrandId` (server seed) and the brand gate's
 * `shouldPersistReadyBrand` — so the app scopes to the new brand IMMEDIATELY
 * while it extracts, instead of staying on whichever brand happens to be the
 * newest READY one. The gate consumes it on the `extracting -> ready` flip, so
 * it survives a reload either way. An org's FIRST brand is already its sole
 * active brand and needs no intent.
 *
 * Org-scoped so a session that switches organizations cannot carry one org's
 * intent into another (the bug the unsuffixed `brew_brand_intent` cookie had).
 */
export function writeBrandIntentCookie(orgId: string, brandId: string): void {
  writeCookie(brandIntentCookieName(orgId), encodeURIComponent(brandId))
}

export function clearBrandIntentCookie(orgId: string): void {
  deleteCookie(brandIntentCookieName(orgId))
}

export function readBrandIntentCookie(orgId: string): string | null {
  return readCookie(brandIntentCookieName(orgId))
}

/** Create a fresh URL-stable setup session for either onboarding mode. */
export function buildSetupSessionHref(isAddBrand: boolean): string {
  const session = crypto.randomUUID()
  const query = isAddBrand
    ? new URLSearchParams({ mode: 'add', session })
    : new URLSearchParams({ session })
  return `/setup?${query.toString()}`
}

/**
 * Every add-brand entry gets a URL-stable UUID. It is both the React remount
 * key and the namespace for durable setup cookies throughout that one wizard.
 */
export function buildAddBrandSetupHref(): string {
  return buildSetupSessionHref(true)
}
